import { PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { CourseDto, PaginationMetaDto, PublicCourseDto } from 'api/generated';

import {
  changeUseFirstPromoCodeThunk,
  createInstallmentPaymentThunk,
  pauseSubscriptionThunk,
  purchaseCourseInstallmentThunk,
  unpauseInstallmentThunk,
  unpauseSubscriptionThunk,
  validateInstallmentPricingThunk,
} from '../payments/actions';
import {
  cancelSubscriptionDowngradeThunk,
  cancelSubscriptionTrialDowngradeThunk,
  changeCourseSubscribeThunk,
  checkPromoCodeThunk,
  getActiveCourseExamDateThunk,
  getActiveCourseThunk,
  getCheatSheetThunk,
  getCheckPurchaseThunk,
  getCourseByIdPublicThunk,
  getCourseByIdThunk,
  getCoursesThunk,
  getMyCoursesThunk,
  getTextBookThunk,
  newSubscribeCourseThunk,
  purchaseCourseFromTrialThunk,
  purchaseCourseThunk,
  resetCourseResultsThunk,
  setActiveCourseExamDateThunk,
  setActiveCourseThunk,
  subscribeCourseFromTrialThunk,
  subscribeCourseThunk,
  unsubscribeCourseThunk,
} from './actions';
import { CourseOptionsForPayment, PaymentOptions, UnsubscribeSteps } from './types';

type CourseDtoExtended = {
  domainButtonName?: string | null;
  domainStatsButtonName?: string | null;
  chapterButtonName?: string | null;
  chapterStatsButtonName?: string | null;
  bundleUpsellId?: string | null;
  upsellOptionIds?: string[] | null;
  upsellCustomName?: string | null;
  upsellDescription?: string | null;
  upsellPrimaryColor?: string | null;
  upsellHoverColor?: string | null;
  upsellBorderColor?: string | null;
  upsellSubtitle?: string | null;
  upsellSubtext?: string | null;
  upsellBadgeBackground?: string | null;
  upsellBadgeText?: string | null;
  // Lottie JSON stored as jsonb in API; keep loose typing here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  upsellAnimation?: any;
  upsellAnimationUrl?: string | null;
  upsellAnimationKey?: string | null;
  pricingAnimationGoldKey?: string | null;
  pricingAnimationSilverKey?: string | null;
  pricingAnimationBronzeKey?: string | null;
  pricingAnimationGoldUrl?: string | null;
  pricingAnimationSilverUrl?: string | null;
  pricingAnimationBronzeUrl?: string | null;
  pricingAnimationBonusesUrl?: string | null;
  pricingAnimationPointerUrl?: string | null;
} & CourseDto;

interface CoursesSlice {
  courses: CourseDto[] | null;
  idCourse: string | null;
  meta: PaginationMetaDto | null;
  activeCourse: CourseDtoExtended | null;
  previousActiveCourse?: CourseDtoExtended | null;
  inviteCourse: CourseDto | null;
  paymentOption: PaymentOptions | null;
  course: CourseDto | null;
  publicCourse: PublicCourseDto | null;
  cheatSheetUrl: string | null;
  textBookUrl: string | null;
  isChooseProductModalOpen: boolean;
  isBundleUpgradeOptionsModalOpen: boolean;
  bundleUpgradeData: {
    bundle: CourseDto;
    bundleCourses: CourseDto[];
    isUpsellModal?: boolean;
    upsellOrderIds?: string[];
  } | null;
  /**
   * The Bundle Builder replaces the legacy upsell modal for courses configured with
   * upsellMode='builder'. It is a full-page modal rather than a route, matching how
   * ChooseProductModal already renders, so the handoff to checkout stays in the same surface.
   */
  isBundleBuilderModalOpen: boolean;
  bundleBuilderSourceCourseId: string | null;
  /**
   * Seeds the builder with an existing selection — the bundle a learner is trialling or already
   * paying for — so "Change your bundle" opens on their bundle rather than the default package.
   */
  bundleBuilderSeed: { tier: string | null; memberSlugs: string[] } | null;
  /** Certification course ids the learner already holds PAID access to; the picker badges them. */
  bundleBuilderOwnedCertIds: string[];
  /**
   * The chooser is the branch point of both upgrade flows: upgrade this course, or deal in
   * bundles. It fetches the learner's server-side bundle context when it opens — which flow to
   * show is a per-learner fact no course-level flag can carry.
   */
  isUpgradeChooserModalOpen: boolean;
  upgradeChooserSourceCourseId: string | null;
  /** The simplified installments-or-lifetime step between the builder and checkout. */
  isBundlePaymentChoiceModalOpen: boolean;
  /**
   * True only while the open checkout was reached FROM the payment-choice step, so it can offer
   * a back arrow to change the payment method. Generic checkout entries (pricing table) clear it.
   */
  paymentModalFromBundleChoice: boolean;
  isPaymentModalOpen: boolean;
  courseOptionsForPayment: CourseOptionsForPayment | null;
  isPaymentErrorModalOpen: boolean;
  examDate: string | null;
  isPurchased: boolean;
  promocode: boolean;
  useFirstMonthDiscount: boolean;
  unsubscribeStep: UnsubscribeSteps;
  isPauseSubscription: boolean;
  isPauseCertificate: boolean;
  setIsSuccessfulUnsubscribe: boolean;
  isDisabledPaymentButton: boolean;
  openUnsubscribeOnChooseProductModal: boolean;
}

const initialState: CoursesSlice = {
  courses: null,
  course: null,
  idCourse: null,
  publicCourse: null,
  meta: null,
  activeCourse: null,
  previousActiveCourse: null,
  paymentOption: null,
  inviteCourse: null,
  isChooseProductModalOpen: false,
  isBundleUpgradeOptionsModalOpen: false,
  isBundleBuilderModalOpen: false,
  bundleBuilderSourceCourseId: null,
  bundleBuilderSeed: null,
  bundleBuilderOwnedCertIds: [],
  isUpgradeChooserModalOpen: false,
  upgradeChooserSourceCourseId: null,
  isBundlePaymentChoiceModalOpen: false,
  paymentModalFromBundleChoice: false,
  bundleUpgradeData: null,
  isPaymentModalOpen: false,
  isPaymentErrorModalOpen: false,
  courseOptionsForPayment: null,
  cheatSheetUrl: null,
  textBookUrl: null,
  examDate: null,
  isPurchased: false,
  useFirstMonthDiscount: false,
  promocode: false,
  unsubscribeStep: UnsubscribeSteps.STEP_1,
  isPauseSubscription: false,
  isPauseCertificate: false,
  setIsSuccessfulUnsubscribe: false,
  isDisabledPaymentButton: false,
  openUnsubscribeOnChooseProductModal: false,
};

export const coursesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    selectCourse: (state, { payload }: PayloadAction<CourseDto | null>) => ({ ...state, course: payload }),
    inviteCourse: (state, { payload }: PayloadAction<CourseDto | null>) => ({ ...state, inviteCourse: payload }),
    openChooseProductModalOpen: (state) => ({ ...state, isChooseProductModalOpen: true }),
    closeChooseProductModalOpen: (state) => ({ ...state, isChooseProductModalOpen: false }),
    openBundleUpgradeOptionsModal: (
      state,
      {
        payload,
      }: PayloadAction<{
        bundle: CourseDto;
        bundleCourses: CourseDto[];
        isUpsellModal?: boolean;
        upsellOrderIds?: string[];
      }>,
    ) => ({
      ...state,
      isBundleUpgradeOptionsModalOpen: true,
      bundleUpgradeData: payload,
    }),
    closeBundleUpgradeOptionsModal: (state) => ({
      ...state,
      isBundleUpgradeOptionsModalOpen: false,
      bundleUpgradeData: null,
    }),
    openBundleBuilderModal: (
      state,
      {
        payload,
      }: PayloadAction<{
        sourceCourseId: string;
        seed?: { tier: string | null; memberSlugs: string[] } | null;
        ownedCertIds?: string[];
      }>,
    ) => ({
      ...state,
      isBundleBuilderModalOpen: true,
      bundleBuilderSourceCourseId: payload.sourceCourseId,
      bundleBuilderSeed: payload.seed ?? null,
      bundleBuilderOwnedCertIds: payload.ownedCertIds ?? [],
    }),
    closeBundleBuilderModal: (state) => ({
      ...state,
      isBundleBuilderModalOpen: false,
      bundleBuilderSourceCourseId: null,
      bundleBuilderSeed: null,
      bundleBuilderOwnedCertIds: [],
    }),
    openUpgradeChooserModal: (state, { payload }: PayloadAction<{ sourceCourseId: string }>) => ({
      ...state,
      isUpgradeChooserModalOpen: true,
      upgradeChooserSourceCourseId: payload.sourceCourseId,
    }),
    closeUpgradeChooserModal: (state) => ({
      ...state,
      isUpgradeChooserModalOpen: false,
      upgradeChooserSourceCourseId: null,
    }),
    // Chooser -> builder, one commit: only one DefaultModal may be open at a time (each writes
    // document.body.style.overflow, and the second unmount would restore scrolling mid-flow).
    switchFromChooserToBuilder: (
      state,
      {
        payload,
      }: PayloadAction<{
        sourceCourseId: string;
        seed?: { tier: string | null; memberSlugs: string[] } | null;
        ownedCertIds?: string[];
      }>,
    ) => ({
      ...state,
      isUpgradeChooserModalOpen: false,
      upgradeChooserSourceCourseId: null,
      isBundleBuilderModalOpen: true,
      bundleBuilderSourceCourseId: payload.sourceCourseId,
      bundleBuilderSeed: payload.seed ?? null,
      bundleBuilderOwnedCertIds: payload.ownedCertIds ?? [],
    }),
    // Chooser -> the course's own pricing table (the "upgrade this course" half of Flow 1).
    switchFromChooserToPricing: (state) => ({
      ...state,
      isUpgradeChooserModalOpen: false,
      upgradeChooserSourceCourseId: null,
      isChooseProductModalOpen: true,
    }),
    // Chooser -> payment choice directly, skipping the builder ("buy the bundle you're trialling").
    switchFromChooserToPaymentChoice: (state) => ({
      ...state,
      isUpgradeChooserModalOpen: false,
      upgradeChooserSourceCourseId: null,
      isBundlePaymentChoiceModalOpen: true,
    }),
    // Builder -> payment choice once picks resolve to a bundle. Replaces the old handoff to the
    // full pricing table: a bundle buyer has already chosen WHAT to buy, only HOW to pay remains.
    switchToPaymentChoiceFromBuilder: (state) => ({
      ...state,
      isBundleBuilderModalOpen: false,
      bundleBuilderSeed: null,
      bundleBuilderOwnedCertIds: [],
      isBundlePaymentChoiceModalOpen: true,
    }),
    // Page -> payment choice, for surfaces that resolve a bundle without opening the builder modal
    // first (the mini builder on Add new course). selectCourse must already hold the bundle.
    openBundlePaymentChoiceModal: (state) => ({
      ...state,
      isBundlePaymentChoiceModalOpen: true,
    }),
    closeBundlePaymentChoiceModal: (state) => ({
      ...state,
      isBundlePaymentChoiceModalOpen: false,
    }),
    // Hand off to the pricing/payment modal once picks resolve to a bundle. Mirrors
    // switchToProductModalFromUpsell: only one DefaultModal may be open at a time, because they
    // each write document.body.style.overflow and the second unmount would restore scrolling.
    switchToProductModalFromBuilder: (state) => ({
      ...state,
      isBundleBuilderModalOpen: false,
      isChooseProductModalOpen: true,
    }),
    // New action to transition from bundle upgrade modal to product modal while preserving upsell data
    switchToProductModalFromUpsell: (state) => {
      console.log(
        '🔄 Redux: switchToProductModalFromUpsell called, preserving bundleUpgradeData:',
        state.bundleUpgradeData,
      );
      return {
        ...state,
        isBundleUpgradeOptionsModalOpen: false,
        isChooseProductModalOpen: true,
        // Keep bundleUpgradeData intact so components know they're in upsell mode
      };
    },
    setIdCourse: (state, { payload }) => {
      state.idCourse = payload;
    },
    selectCourseOptionsForPayment: (state, { payload }: PayloadAction<CourseOptionsForPayment | null>) => ({
      ...state,
      courseOptionsForPayment: payload,
    }),
    openPaymentModalOpen: (state) => ({
      ...state,
      isPaymentModalOpen: true,
      // Generic entry (pricing table etc.) — there is no step to go back to.
      paymentModalFromBundleChoice: false,
    }),
    closePaymentModalOpen: (state) => ({
      ...state,
      isPaymentModalOpen: false,
      paymentModalFromBundleChoice: false,
    }),
    // Payment choice -> checkout, remembering the way back. One reducer, not two dispatches:
    // only one DefaultModal may be open at a time (each writes document.body.style.overflow).
    switchToPaymentFromPaymentChoice: (state) => ({
      ...state,
      isBundlePaymentChoiceModalOpen: false,
      isPaymentModalOpen: true,
      paymentModalFromBundleChoice: true,
    }),
    // Checkout's back arrow -> the installments-or-lifetime step, to change the payment method.
    switchBackToPaymentChoiceFromPayment: (state) => ({
      ...state,
      isPaymentModalOpen: false,
      paymentModalFromBundleChoice: false,
      isBundlePaymentChoiceModalOpen: true,
    }),
    closePaymentErrorModal: (state) => ({
      ...state,
      isPaymentErrorModalOpen: false,
    }),
    setUnsubscribeStep: (state, { payload }: PayloadAction<UnsubscribeSteps>) => {
      state.unsubscribeStep = payload;
    },
    setPauseSubscription: (state, { payload }) => {
      state.isPauseSubscription = payload;
    },
    setPauseCertificate: (state, { payload }) => {
      state.isPauseCertificate = payload;
    },
    setIsSuccessfulUnsubscribe: (state, { payload }) => {
      state.setIsSuccessfulUnsubscribe = payload;
    },
    setDisabledStatus: (state, { payload }) => {
      state.isDisabledPaymentButton = payload;
    },
    setPaymentOption: (state, { payload }) => {
      state.paymentOption = payload;
    },
    setUseFirstMonthDiscount: (state, { payload }) => {
      state.useFirstMonthDiscount = payload;
    },
    setOpenUnsubscribeOnChooseProductModal: (state, { payload }: PayloadAction<boolean>) => {
      state.openUnsubscribeOnChooseProductModal = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyCoursesThunk.pending, (state) => {
        state.courses = null;
      })
      .addCase(getMyCoursesThunk.fulfilled, (state, { payload }) => {
        state.courses = payload;
      })
      .addCase(getCourseByIdThunk.fulfilled, (state, { payload }) => {
        state.course = payload;
      })
      .addCase(getCourseByIdPublicThunk.fulfilled, (state, { payload }) => {
        state.publicCourse = payload;
      })
      .addCase(getActiveCourseThunk.fulfilled, (state, { payload }) => {
        state.activeCourse = { ...state.activeCourse, ...payload };
      })
      .addCase(setActiveCourseThunk.pending, (state, action) => {
        const targetCourseId = typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.courseId;
        const fallbackAccent = typeof action.meta.arg === 'object' ? action.meta.arg.accentColor : undefined;

        state.previousActiveCourse = state.activeCourse;

        const matchingCourse =
          state.courses?.find((course) => course.id === targetCourseId) ||
          (state.courses
            ?.flatMap((c) => c.bundleCoursesAsBundle?.map((b) => b.course) || [])
            .find((c) => c?.id === targetCourseId) as CourseDto | undefined);

        if (matchingCourse) {
          state.activeCourse = {
            ...(state.activeCourse || {}),
            ...matchingCourse,
            ...(fallbackAccent ? { upsellPrimaryColor: fallbackAccent } : {}),
            isActive: true,
          } as CourseDtoExtended;

          state.courses =
            state.courses?.map((course) => ({
              ...course,
              isActive: course.id === targetCourseId,
            })) || null;
        } else if (state.activeCourse && (fallbackAccent || targetCourseId)) {
          state.activeCourse = {
            ...state.activeCourse,
            id: targetCourseId,
            ...(fallbackAccent ? { upsellPrimaryColor: fallbackAccent } : {}),
            isActive: true,
          };
        }
      })
      .addCase(setActiveCourseThunk.rejected, (state) => {
        if (state.previousActiveCourse) {
          const prevId = state.previousActiveCourse.id;
          state.activeCourse = state.previousActiveCourse;
          state.courses =
            state.courses?.map((course) => ({
              ...course,
              isActive: course.id === prevId,
            })) || null;
          state.previousActiveCourse = null;
        }
      })
      .addCase(setActiveCourseThunk.fulfilled, (state, { payload }) => {
        state.previousActiveCourse = null;
        state.activeCourse = payload;
        state.courses =
          state.courses?.map((course) =>
            course.id === payload.id
              ? payload
              : {
                  ...course,
                  isActive: false,
                },
          ) || null;
      })
      .addCase(getCheatSheetThunk.fulfilled, (state, { payload }) => {
        state.cheatSheetUrl = payload.url || '';
      })
      .addCase(checkPromoCodeThunk.fulfilled, (state, { payload }) => {
        state.promocode = payload.valid;
      })
      .addCase(getTextBookThunk.fulfilled, (state, { payload }) => {
        state.textBookUrl = payload.url || '';
      })
      .addCase(subscribeCourseThunk.fulfilled, (state, { payload }) => {
        state.activeCourse = payload;
        state.course = payload;

        state.courses =
          state.courses?.map((course) =>
            course.id === payload.id
              ? payload
              : {
                  ...course,
                  isActive: false,
                },
          ) || null;
      })
      .addCase(subscribeCourseFromTrialThunk.fulfilled, (state, { payload }) => {
        state.activeCourse = payload;
        state.course = payload;

        state.courses =
          state.courses?.map((course) =>
            course.id === payload.id
              ? payload
              : {
                  ...course,
                  isActive: false,
                },
          ) || null;
      })
      .addCase(changeCourseSubscribeThunk.fulfilled, (state, { payload }) => {
        state.activeCourse = payload;
        state.course = payload;

        state.courses =
          state.courses?.map((course) =>
            course.id === payload.id
              ? payload
              : {
                  ...course,
                  isActive: false,
                },
          ) || null;
      })
      // .addCase(purchaseCourseThunk.fulfilled, (state, { payload }) => {
      //   state.activeCourse = payload;
      //   state.course = payload;

      //   state.courses =
      //     state.courses?.map((course) =>
      //       course.id === payload.id
      //         ? payload
      //         : {
      //             ...course,
      //             isActive: false,
      //           },
      //     ) || null;
      // })
      .addCase(purchaseCourseFromTrialThunk.fulfilled, (state, { payload }) => {
        state.activeCourse = payload;
        state.course = payload;

        state.courses =
          state.courses?.map((course) =>
            course.id === payload.id
              ? payload
              : {
                  ...course,
                  isActive: false,
                },
          ) || null;
      })
      .addCase(unsubscribeCourseThunk.fulfilled, (state, { payload }) => {
        if (state.course?.id === payload.id) {
          state.course = payload;
        }
        if (state.activeCourse?.id === payload.id) {
          state.activeCourse = payload;
        }

        state.courses = state.courses?.map((course) => (course.id === payload.id ? payload : course)) || null;
      })
      .addCase(getCoursesThunk.pending, (state, { meta }) => {
        const isFirstPage = !meta.arg.page || meta.arg.page === 1;

        if (isFirstPage) {
          state.courses = null;
          state.meta = null;
        }
      })
      .addCase(getCoursesThunk.fulfilled, (state, { payload }) => {
        const { items, meta } = payload.data;
        const courses = state.courses || [];

        state.courses = payload.isFirstFetch ? items : [...courses, ...items];
        state.meta = meta;
      })
      .addCase(getActiveCourseExamDateThunk.fulfilled, (state, { payload }) => {
        state.examDate = payload;
      })
      .addCase(setActiveCourseExamDateThunk.fulfilled, (state, { payload: { examDate } }) => {
        state.examDate = examDate;
      })
      .addCase(getCheckPurchaseThunk.fulfilled, (state, { payload }) => {
        state.isPurchased = payload.isPurchased;
      })
      .addCase(pauseSubscriptionThunk.fulfilled, (state, { payload }) => {
        const course = state?.courses?.find((course) => course.id === state.idCourse);

        if (course && course.currentPayment) {
          course.currentPayment.onPauseUntil = payload.onPauseUntil;
        }
      })
      .addCase(unpauseSubscriptionThunk.fulfilled, (state, { payload }) => {
        const course = state?.courses?.find((course) => course.id === state.idCourse);
        if (course && course.currentPayment) {
          course.currentPayment.onPauseUntil = payload.onPauseUntil;
        }
      })
      .addMatcher(
        isAnyOf(cancelSubscriptionDowngradeThunk.fulfilled, cancelSubscriptionTrialDowngradeThunk.fulfilled),
        (state, { payload }) => {
          state.activeCourse = payload;
          state.course = payload;

          state.courses = state.courses?.map((course) => (course.id === payload.id ? payload : course)) || null;
        },
      );
  },
});

export const actions = {
  ...coursesSlice.actions,
  getMyCourses: getMyCoursesThunk,
  subscribeCourse: subscribeCourseThunk,
  subscribeCourseFromTrial: subscribeCourseFromTrialThunk,
  purchaseCourse: purchaseCourseThunk,
  purchaseCourseFromTrial: purchaseCourseFromTrialThunk,
  getCourseById: getCourseByIdThunk,
  getCourseByIdPublic: getCourseByIdPublicThunk,
  getActiveCourse: getActiveCourseThunk,
  setActiveCourse: setActiveCourseThunk,
  resetCourseResults: resetCourseResultsThunk,
  getCheatSheet: getCheatSheetThunk,
  getTextBook: getTextBookThunk,
  getCourses: getCoursesThunk,
  unsubscribeCourse: unsubscribeCourseThunk,
  changeCourseSubscribe: changeCourseSubscribeThunk,
  changeUseFirstPromoCode: changeUseFirstPromoCodeThunk,
  cancelSubscriptionDowngrade: cancelSubscriptionDowngradeThunk,
  cancelSubscriptionTrialDowngrade: cancelSubscriptionTrialDowngradeThunk,
  getActiveCourseExamDate: getActiveCourseExamDateThunk,
  setActiveCourseExamDate: setActiveCourseExamDateThunk,
  getCheckPurchase: getCheckPurchaseThunk,
  checkPromoCodeThunk: checkPromoCodeThunk,
  newSubscribeCourse: newSubscribeCourseThunk,
  purchaseCourseInstallment: purchaseCourseInstallmentThunk,
  unpauseInstallment: unpauseInstallmentThunk,
  // NEW: Optimized two-phase installment actions
  validateInstallmentPricing: validateInstallmentPricingThunk,
  createInstallmentPayment: createInstallmentPaymentThunk,
};

export const { reducer } = coursesSlice;
